import axios, { AxiosRequestConfig, AxiosResponse, Method, ResponseType } from "axios";
import { ENDPOINT } from "../api/EndPoints.tsx";

// Define types for API responses and parameters
interface ApiParams {
  [key: string]: any;
}

interface ApiData {
  [key: string]: any;
}

interface TokenResponse {
  TOKEN?: string;
}

interface UserParam {
  TOKEN?: string;
}

interface PersistUser {
  USER_PARAM?: string;
}

// Token retrieval function
const getToken = async (maxAttempts: number = 3, delay: number = 500): Promise<string | null> => {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const userPersist = localStorage.getItem("persist:user");
      if (userPersist) {
        const parsedUser: PersistUser = JSON.parse(userPersist);
        if (parsedUser.USER_PARAM) {
          const userParam: UserParam = JSON.parse(parsedUser.USER_PARAM);
          if (userParam?.TOKEN) {
            return userParam.TOKEN;
          }
        }
      }
    } catch (error) {
      console.error("Error retrieving token from localStorage:", error);
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
  return null;
};

// Initialize token
let token: string | null = await getToken();

// GET API Function
const externalgetApi = async <T = any>(
  endpoint: string,
  method: Method = "GET",
  params: ApiParams = {},
  data: ApiData = {},
  responseType: ResponseType = "json"
): Promise<T> => {
  const url = `${ENDPOINT}${endpoint}`;

  const config: AxiosRequestConfig = {
    method,
    url,
    params,
    data,
    responseType,
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
    },
  };

  try {
    const response: AxiosResponse<T> = await axios(config);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 401) {
      console.warn("Token might not be ready, retrying...");
      token = await getToken();
      config.headers = {
        ...config.headers,
        Authorization: token ? `Bearer ${token}` : "",
      };
      const response: AxiosResponse<T> = await axios(config);
      return response.data;
    }
    console.error("API call error:", error);
    throw error;
  }
};

// POST API Function
const externalpostApi = async <T = any>(
  endpoint: string,
  data: ApiData = {},
  responseType: ResponseType = "json"
): Promise<T> => {
  const url = `${ENDPOINT}${endpoint}`;

  const config: AxiosRequestConfig = {
    method: "POST",
    url,
    data,
    responseType,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    console.log("Request data:", data);
    const response: AxiosResponse<T> = await axios(config);
    console.log("POST API Response data:", response.data);
    return response.data;
  } catch (error: any) {
    console.error(
      "API call error:",
      error.response ? error.response.data : error.message
    );
    throw error;
  }
};

// POST API Function for multipart/form-data (images/files)
const externalImagepostApi = async <T = any>(
  endpoint: string,
  data: FormData | ApiData = {},
  responseType: ResponseType = "json"
): Promise<T> => {
  const url = `${ENDPOINT}${endpoint}`;

  const config: AxiosRequestConfig = {
    method: "POST",
    url,
    data,
    responseType,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    console.log("Request data:", data);
    const response: AxiosResponse<T> = await axios(config);
    console.log("POST API Response data:", response.data);
    return response.data;
  } catch (error: any) {
    console.error(
      "API call error:",
      error.response ? error.response.data : error.message
    );
    throw error;
  }
};

// PUT API Function
const externalputApi = async <T = any>(
  endpoint: string,
  data: ApiData = {},
  responseType: ResponseType = "json"
): Promise<T> => {
  const url = `${ENDPOINT}${endpoint}`;

  const config: AxiosRequestConfig = {
    method: "PUT",
    url,
    data,
    responseType,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response: AxiosResponse<T> = await axios(config);
    return response.data;
  } catch (error: any) {
    console.error(
      "API call error:",
      error.response ? error.response.data : error.message
    );
    throw error;
  }
};

// DELETE API Function
const externaldeleteApi = async <T = any>(
  endpoint: string,
  method: Method = "DELETE",
  params: ApiParams = {},
  data: ApiData = {},
  responseType: ResponseType = "json"
): Promise<T> => {
  const url = `${ENDPOINT}${endpoint}`;

  const config: AxiosRequestConfig = {
    method,
    url,
    params,
    data,
    responseType,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response: AxiosResponse<T> = await axios(config);
    return response.data;
  } catch (error: any) {
    console.error("API call error:", error);
    throw error;
  }
};

// DELETE API Function with query parameters
const externaldeletecomApi = async <T = any>(
  endpoint: string,
  params: ApiParams = {},
  responseType: ResponseType = "json"
): Promise<T> => {
  const url = `${ENDPOINT}${endpoint}`;

  const config: AxiosRequestConfig = {
    method: "DELETE",
    url,
    params,
    responseType,
    headers: {
      Authorization: `Bearer ${token}`,
      accept: "*/*",
    },
  };

  try {
    const response: AxiosResponse<T> = await axios(config);
    console.log("DELETE API Response data:", response.data);
    return response.data;
  } catch (error: any) {
    console.error(
      "DELETE API error:",
      error.response ? error.response.data : error.message
    );
    throw error;
  }
};

export default externaldeletecomApi;
export { externalgetApi, externalpostApi, externalputApi, externaldeleteApi, externalImagepostApi };